import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "pwa_banner_dismissed";

/**
 * Returns install-prompt state for the PWA install banner.
 *
 * - `isStandalone` — already running as an installed PWA
 * - `isInstallable` — browser has held a `beforeinstallprompt` event and the
 *   user hasn't dismissed the banner this session
 * - `triggerInstall` — show the native install dialog
 * - `dismiss` — hide the banner for this session
 */
export function useInstallPrompt() {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "1"
  );

  useEffect(() => {
    // Detect PWA display mode
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const standalone =
      mediaQuery.matches || (navigator as { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);

    const onDisplayModeChange = (e: MediaQueryListEvent) => {
      setIsStandalone(e.matches);
    };
    mediaQuery.addEventListener("change", onDisplayModeChange);

    // Capture the install prompt
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Clear once installed
    const onInstalled = () => setPromptEvent(null);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      mediaQuery.removeEventListener("change", onDisplayModeChange);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async (): Promise<boolean> => {
    if (!promptEvent) return false;
    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "accepted") setPromptEvent(null);
    return outcome === "accepted";
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return {
    isStandalone,
    isInstallable: !!promptEvent && !dismissed,
    triggerInstall,
    dismiss,
  };
}
